import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { theme, notification, Popover } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { RootState } from 'store/configureStore';
import { clientReqOp } from 'store/ducks/ClientReq';
import {
  IApplicability,
  IAurReq,
  ICategory,
  IClientReq,
  INewClientReqs,
  IRegulatoryReq
} from 'store/ducks/ClientReq/types';
import { CategoryList, ThemeList, YesNo } from 'store/ducks/globalTypes';
import { applicabilityOptions } from 'constants/filterOptions';
import { getDynamicCategories } from 'constants/categorieNames';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import { useFilterMapping } from 'utils/hooks/useFilterMapping';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import CustomCheckbox from 'components/CustomCheckbox';
import CustomSelect from 'components/CustomSelect';
import ComplianceEstLayout from 'components/Layouts/ComplianceEstLayout';
import Helpers from 'utils/Helpers';
import TextAreaBox from 'components/TextAreaBox_clnReqScreen';
import ApiService from 'utils/ApiCalls';
import i18n from 'constants/translation/i18n';
import * as Styled from './ClientRequirements.styled';
import useDataProcessor from './useDataProcessor';
import FilterDropdown from 'components/FilterDropdown';

// Helper function to parse text and separate numbers from content
const parseTextWithNumbers = (text: string | undefined, scheme: string) => {
  if (!text) return null;

  const match = text.match(
    /^([0-9]+\.|\([0-9]+\)|\([a-zA-Z]+\)|[a-zA-Z]+\))(.*)$/
  );

  if (match) {
    const number = match[1];
    const content = match[2];
    return (
      <>
        <Styled.NumberSpan scheme={scheme}>{number}</Styled.NumberSpan>
        <Styled.TextSpan scheme={scheme}>{content}</Styled.TextSpan>
      </>
    );
  }

  return null;
};

// Helper function to render multi-line text with different styles
const renderFormattedText = (
  text: string | undefined,
  scheme: string,
  token: ReturnType<typeof useToken>['token']
) => {
  if (!text) return null;

  const lines = text.split('\n').filter((line) => line.trim());

  return (
    <>
      {lines.map((line, index) => {
        const trimmedLine = line.trim();
        let LineComponent = Styled.SubItemLine;

        if (trimmedLine.length < 100 && !/^[0-9(]/.test(trimmedLine)) {
          LineComponent = Styled.TitleTextLine;
        } else if (/^[0-9]+\./.test(trimmedLine)) {
          LineComponent = Styled.NumberedParagraphLine;
        }
        const parsed = parseTextWithNumbers(line, scheme);

        return (
          <LineComponent key={index} scheme={scheme} token={token}>
            {parsed || line}
          </LineComponent>
        );
      })}
    </>
  );
};

const { useToken } = theme;

const ClientRequirements: React.FC = () => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const { applyFiltersToData, isValidBruId } = useFilterMapping();

  // Use the reusable data processor
  const {
    normalizeAurReqs,
    processFilteredData,
    getCategoryFromReq,
    processAppliedFiltersResult,
    useClickOutside,
    findCategoryFromAppliedFilters
  } = useDataProcessor();

  const {
    lang,
    drawTable,
    appliedFiltersResult,
    tableFilters,
    checkedElementsToBeMerged,
    checkedElementsToBeCopied,
    mergedElements,
    copiedElements,
    checkedAurReqs,
    saveClicked,
    clientReqs,
    loading,
    updated,
    scheme,
    orgContext,
    mainOrganization
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    appliedFiltersResult: state.clientReq.appliedFiltersResult,
    tableFilters: state.clientReq.tableFilters,
    checkedElementsToBeMerged: state.clientReq.checkedElementsToBeMerged,
    checkedElementsToBeCopied: state.clientReq.checkedElementsToBeCopied,
    mergedElements: state.clientReq.mergedElements,
    copiedElements: state.clientReq.copiedElements,
    checkedAurReqs: state.clientReq.checkedAurReqs,
    saveClicked: state.complianceEst.saveClicked,
    clientReqs: state.clientReq.clientReqs,
    loading: state.clientReq.loading,
    updated: state.clientReq.updated,
    scheme: state.global.scheme,
    orgContext: state.global.hierarchicalLevelsScope,
    mainOrganization: state.global.mainOrganization
  }));
  const [changedApplicability, setChangedApplicability] = useState<{
    [key: number]: IApplicability;
  }>({});
  const [changedCategories, setChangedCategory] = useState<{
    [key: string]: ICategory;
  }>({});
  const [changedClientCategories, setChangedClientCategory] = useState<{
    [key: string]: { clnReqKey: string; category: CategoryList };
  }>({});
  // New state for AUR-level categories
  const [changedAurCategories, setChangedAurCategory] = useState<{
    [aurId: number]: CategoryList;
  }>({});
  const filterParams = Helpers.toApiFilters(orgContext);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const topScrollbarContentRef = useRef<HTMLDivElement>(null);

  const { handleDelete: handleBruDelete } = useDeleteWithDependencyCheck({
    tableName: 'business_rules',
    softDeleteFn: (id) => {
      const isTemporary = String(id).startsWith('new-');
      if (isTemporary) {
        return Promise.resolve({ data: {} });
      }
      return ApiService.softDeleteRecord('business_rules', id);
    },
    onSuccess: (id) =>
      dispatch(
        clientReqOp.setClientReqs(
          Object.fromEntries(
            Object.entries(clientReqs).filter(([key]) => Number(key) !== id)
          )
        )
      ),
    entityLabel: 'baseline definition',
    lang
  });
  const [categoryOptions, setCategoryOptions] = useState<
    { value: CategoryList; label: string }[]
  >([]);
  const [aurSearchText, setAurSearchText] = useState<string>('');
  const [showAurSearch, setShowAurSearch] = useState<boolean>(false);
  const [clientSearchText, setClientSearchText] = useState<string>('');
  const [showClientSearch, setShowClientSearch] = useState<boolean>(false);
  const [regReqSearchText, setRegReqSearchText] = useState<string>('');
  const [showRegReqSearch, setShowRegReqSearch] = useState<boolean>(false);
  const [localCheckedAurReqs, setLocalCheckedAurReqs] = useState<number[]>([]);
  const [contentTypeOptions, setContentTypeOptions] = useState<
    { value: number; label: string }[]
  >([]);
  const [selectedContentType, setSelectedContentType] = useState<{
    [key: number]: number;
  }>({});
  const [contentTypeDefinition, setContentTypeDefinition] = useState<{
    [key: number]: { [contentTypeId: number]: string };
  }>({});
  const [changedContentType, setChangedContentType] = useState<{
    [key: number]: boolean;
  }>({});
  const [showCategoryFilter, setShowCategoryFilter] = useState<boolean>(false);
  const [categoryFilter, setCategoryFilter] = useState<CategoryList | 'all'>(
    'all'
  );
  const [scrollLeft, setScrollLeft] = useState(0);
  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const [showScopeFilter, setShowScopeFilter] = useState<boolean>(false);
  const [scopeFilter, setScopeFilter] = useState<'all' | 'Y' | 'N'>('all');
  const scopeFilterRef = useRef<HTMLDivElement>(null);
  const [initialCopiedItems, setInitialCopiedItems] = useState<number[]>([]);
  const [forceUpdateKey, setForceUpdateKey] = useState<number>(0);
  const [userDeselectedItems, setUserDeselectedItems] = useState<number[]>(
    () => {
      const stored = localStorage.getItem('userDeselectedCopyItems');
      return stored ? JSON.parse(stored) : [];
    }
  );
  const [aurDragOffset, setAurDragOffset] = useState<{ [key: number]: number }>(
    {}
  );

  const handleAurDrag = (aurReqId: number, offsetX: number) => {
    setAurDragOffset((prev) => ({
      ...prev,
      [aurReqId]: offsetX
    }));
  };

  const hasChanges = useMemo(() => {
    const hasApplicabilityChanges =
      Object.keys(changedApplicability).length > 0;
    const hasCategoryChanges = Object.keys(changedCategories).length > 0;
    const hasClientCategoryChanges =
      Object.keys(changedClientCategories).length > 0;
    const hasAurCategoryChanges = Object.keys(changedAurCategories).length > 0;
    const hasContentTypeChanges = Object.keys(changedContentType).length > 0;
    const hasClientReqChanges = Object.values(clientReqs).some(
      (req) => Object.keys(req.updated_client_reqs || {}).length > 0
    );

    return (
      hasApplicabilityChanges ||
      hasCategoryChanges ||
      hasClientCategoryChanges ||
      hasAurCategoryChanges ||
      hasContentTypeChanges ||
      hasClientReqChanges
    );
  }, [
    changedApplicability,
    changedCategories,
    changedClientCategories,
    changedAurCategories,
    changedContentType,
    clientReqs
  ]);

  useUnsavedChanges({ hasChanges });

  const isAurReqUnchecked = (aurReq: IAurReq) =>
    !localCheckedAurReqs.includes(aurReq.id);

  // Get all available AUR requirement IDs
  const getAllAurReqIds = (): number[] => {
    const allIds: number[] = [];
    processAppliedFiltersResult(appliedFiltersResult, (regReq, aurReq) => {
      if (aurReq.id > 0) {
        allIds.push(aurReq.id);
      }
    });
    return allIds;
  };

  // Select all scope checkboxes
  const handleSelectAll = () => {
    const allIds = getAllAurReqIds();
    setLocalCheckedAurReqs(allIds);
    const newChangedApplicability: { [key: number]: IApplicability } = {};
    allIds.forEach((id) => {
      newChangedApplicability[id] = { applic_value: YesNo.YES, aur_req_id: id };
    });
    setChangedApplicability((prev) => ({
      ...prev,
      ...newChangedApplicability
    }));
  };

  // Deselect all scope checkboxes
  const handleDeselectAll = () => {
    const allIds = getAllAurReqIds();
    setLocalCheckedAurReqs([]);

    // Update changed applicability for all items (keeping internal logic as applicability)
    const newChangedApplicability: { [key: number]: IApplicability } = {};
    allIds.forEach((id) => {
      newChangedApplicability[id] = { applic_value: YesNo.NO, aur_req_id: id };
    });
    setChangedApplicability((prev) => ({
      ...prev,
      ...newChangedApplicability
    }));
  };

  // Render the scope filter dropdown
  const renderScopeDropdown = () => {
    if (!showScopeFilter || !scopeFilterRef.current) return null;

    const rect = scopeFilterRef.current.getBoundingClientRect();
    const dropdownTop = rect.bottom;
    const dropdownLeft = rect.left;

    return createPortal(
      <Styled.DropdownPortal
        className='scope-filter-dropdown'
        $top={dropdownTop}
        $left={dropdownLeft}
      >
        <Styled.FilterTitle>
          {i18n.t('SELECT_DESELECT_SCOPE')}
        </Styled.FilterTitle>
        <Styled.FilterButtonContainer>
          <Styled.FilterButton
            size='small'
            type='primary'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSelectAll();
            }}
          >
            Select All
          </Styled.FilterButton>
          <Styled.FilterButton
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDeselectAll();
            }}
          >
            Deselect All
          </Styled.FilterButton>
        </Styled.FilterButtonContainer>
        {[
          { value: 'all', label: 'All' },
          { value: 'Y', label: 'Yes' },
          { value: 'N', label: 'No' }
        ].map((option) => (
          <Styled.DropdownOption
            key={option.value}
            selected={scopeFilter === option.value}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onMouseUp={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setScopeFilter(option.value as 'all' | 'Y' | 'N');
              dispatch(
                clientReqOp.setTableFilter({
                  ...tableFilters,
                  applicability: [option.value]
                })
              );
            }}
          >
            {option.label}
          </Styled.DropdownOption>
        ))}
        <Styled.FilterButtonContainer>
          <Styled.FilterButton
            size='small'
            type='primary'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowScopeFilter(false);
            }}
          >
            OK
          </Styled.FilterButton>
        </Styled.FilterButtonContainer>
      </Styled.DropdownPortal>,
      document.body
    );
  };

  // Render the category filter dropdown
  const renderCategoryDropdown = () => {
    if (!showCategoryFilter || !categoryFilterRef.current) return null;

    const rect = categoryFilterRef.current.getBoundingClientRect();
    const dropdownTop = rect.bottom + 5;
    const dropdownLeft = rect.left;

    return createPortal(
      <Styled.CategoryDropdownPortal
        className='category-filter-dropdown'
        $top={dropdownTop}
        $left={dropdownLeft}
      >
        {[{ value: 'all', label: 'All Categories' }, ...categoryOptions].map(
          (option) => (
            <Styled.DropdownOption
              key={option.value}
              selected={categoryFilter === option.value}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCategoryFilter(option.value as CategoryList | 'all');
                setShowCategoryFilter(false);
              }}
            >
              {option.label}
            </Styled.DropdownOption>
          )
        )}
      </Styled.CategoryDropdownPortal>,
      document.body
    );
  };

  // Initialize localCheckedAurReqs with existing applicability data
  useEffect(() => {
    if (appliedFiltersResult && appliedFiltersResult.length > 0) {
      const checkedIds: number[] = [];
      const contentTypes: { [key: number]: number } = {};
      const definitions: {
        [key: number]: { [contentTypeId: number]: string };
      } = {};

      processAppliedFiltersResult(appliedFiltersResult, (regReq, aurReq) => {
        if (aurReq.is_app === YesNo.YES) {
          checkedIds.push(aurReq.id);
        }
        // Only update if not already set (preserve user changes)
        if (aurReq.content_type_id && !selectedContentType[aurReq.id]) {
          contentTypes[aurReq.id] = aurReq.content_type_id;
        }
        if (
          aurReq.definition &&
          aurReq.content_type_id &&
          !contentTypeDefinition[aurReq.id]?.[aurReq.content_type_id]
        ) {
          if (!definitions[aurReq.id]) {
            definitions[aurReq.id] = {};
          }
          definitions[aurReq.id][aurReq.content_type_id] = aurReq.definition;
        }
      });

      setLocalCheckedAurReqs(checkedIds);
      setSelectedContentType((prev) => ({ ...prev, ...contentTypes }));
      setContentTypeDefinition((prev) => ({ ...prev, ...definitions }));
    }
  }, [appliedFiltersResult]);

  // Marks all YES applicable AUR requirements as checked

  const setCheckedAurReqs = () => {
    processAppliedFiltersResult(appliedFiltersResult, (regReq, aurReq) => {
      if (aurReq.is_app === YesNo.YES && !checkedAurReqs.includes(aurReq.id)) {
        dispatch(clientReqOp.setCheckedAurReqs(aurReq.id));
      }
    });
  };
  const setClientReqs = () => {
    if (!appliedFiltersResult || appliedFiltersResult.length === 0) return;

    const result = appliedFiltersResult.reduce(
      (acc: INewClientReqs, item: IRegulatoryReq) => {
        if (!item.AUR_REQ) return acc;

        const aurReqs = normalizeAurReqs(item.AUR_REQ);

        aurReqs.forEach((aurReq: IAurReq) => {
          const aurReqId = aurReq.id;

          // Always initialize entry for each AUR_REQ, even if no CLN_REQ exists
          if (!acc[aurReqId]) {
            acc[aurReqId] = {
              client_reqs: {},
              updated_client_reqs: {}
            };
          }

          if (aurReq.CLN_REQ) {
            // Only process if CLN_REQ has actual data (not empty object)
            const clnReqEntries = Object.entries(aurReq.CLN_REQ);
            if (clnReqEntries.length > 0) {
              const clnReqValues = Object.values(aurReq.CLN_REQ).map(
                (clnReq: IClientReq) => ({
                  aur_parent_key: aurReq.aur_parent_key,
                  aur_child_key: aurReq.aur_child_key,
                  value: clnReq.value,
                  bru_id: clnReq.id,
                  // Include category_id from API response
                  ...(clnReq.category_id && {
                    category_id: clnReq.category_id
                  })
                })
              );

              const newClnReqs = clnReqValues.reduce(
                (o, key) => ({ ...o, [key.bru_id]: key }),
                {}
              );

              acc[aurReqId].client_reqs = {
                ...acc[aurReqId].client_reqs,
                ...newClnReqs
              };
            }
          }
        });

        return acc;
      },
      {}
    );

    dispatch(clientReqOp.setClientReqs(result));
  };
  // Removed filterRegReqs - now using consolidated processFilteredData

  const handleCheckboxChange = (id: number, checked: boolean) => {
    setLocalCheckedAurReqs((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      } else {
        return prev.filter((aurId) => aurId !== id);
      }
    });

    setChangedApplicability((prevState) => ({
      ...prevState,
      [id]: { applic_value: checked ? YesNo.YES : YesNo.NO, aur_req_id: id }
    }));
  };

  const handleAurCategoryChange = (aurId: number, value: CategoryList) => {
    setChangedAurCategory((prevState) => ({
      ...prevState,
      [aurId]: value
    }));
  };

  // Replace the old getFilteredDataSource with the consolidated version
  const getFilteredDataSource = () => {
    return processFilteredData(
      appliedFiltersResult || [],
      {
        applicability: tableFilters.applicability as string[],
        searchText: regReqSearchText,
        aurSearchText,
        clientSearchText,
        categoryFilter
      },
      localCheckedAurReqs
    );
  };

  //  Handles merge checkbox toggle
  const handleMergeChange = (aurReqEl: IAurReq) => {
    if (!checkedElementsToBeMerged.includes(aurReqEl.id)) {
      const elements = [...checkedElementsToBeMerged, aurReqEl.id];
      dispatch(clientReqOp.setCheckedElementsToBeMerged(elements));
    } else {
      const elements = checkedElementsToBeMerged.filter(
        (el: number) => el !== aurReqEl.id
      );
      dispatch(clientReqOp.setCheckedElementsToBeMerged(elements));
    }
  };

  const getCopiedItemsFromState = (): number[] => {
    return copiedElements || [];
  };

  const addCopiedItemToState = (aurReqId: number) => {
    const existing = getCopiedItemsFromState();
    if (!existing.includes(aurReqId)) {
      const updatedList = [...existing, aurReqId];
      dispatch(clientReqOp.setCopiedElements(updatedList));
    }
  };

  const isAurReqCopied = (aurReqId: number): boolean => {
    const clientReq = clientReqs[aurReqId];

    if (!clientReq) {
      return false;
    }

    // Check if user has explicitly deselected this item
    if (userDeselectedItems.includes(aurReqId)) {
      return false;
    }

    const updatedReqs = clientReq.updated_client_reqs || {};
    const hasUpdatedReqs = Object.keys(updatedReqs).length > 0;

    const existingReqs = clientReq.client_reqs || {};
    const hasExistingReqs = Object.keys(existingReqs).length > 0;

    const isCopied = hasUpdatedReqs || hasExistingReqs;

    if (isCopied) {
      addCopiedItemToState(aurReqId);
    } else {
      const copiedFromState = getCopiedItemsFromState();
      if (copiedFromState.includes(aurReqId)) {
        const updatedState = copiedFromState.filter((id) => id !== aurReqId);
        dispatch(clientReqOp.setCopiedElements(updatedState));
      }
    }

    return isCopied;
  };

  // Handles copy checkbox toggle
  const handleCopyCheck = (e: CheckboxChangeEvent, aurReqEl: IAurReq) => {
    if (e.target.checked) {
      // Remove from deselected items if user re-checks
      const updatedDeselected = userDeselectedItems.filter(
        (id) => id !== aurReqEl.id
      );
      setUserDeselectedItems(updatedDeselected);
      localStorage.setItem(
        'userDeselectedCopyItems',
        JSON.stringify(updatedDeselected)
      );

      if (!checkedElementsToBeCopied.includes(aurReqEl.id)) {
        const elements = [...checkedElementsToBeCopied, aurReqEl.id];
        dispatch(clientReqOp.setCheckedElementsToBeCopied(elements));
      }
    } else {
      const updatedDeselected = userDeselectedItems.includes(aurReqEl.id)
        ? userDeselectedItems
        : [...userDeselectedItems, aurReqEl.id];
      setUserDeselectedItems(updatedDeselected);
      localStorage.setItem(
        'userDeselectedCopyItems',
        JSON.stringify(updatedDeselected)
      );

      const elements = checkedElementsToBeCopied.filter(
        (el) => el !== aurReqEl.id
      );
      dispatch(clientReqOp.setCheckedElementsToBeCopied(elements));

      const currentClientReqs = { ...clientReqs };
      if (currentClientReqs[aurReqEl.id]) {
        const existingUpdatedReqs =
          currentClientReqs[aurReqEl.id].updated_client_reqs || {};

        const filteredUpdatedReqs = Object.keys(existingUpdatedReqs).reduce(
          (acc, key) => {
            if (!key.startsWith('copy-selected-')) {
              acc[key] = existingUpdatedReqs[key];
            }
            return acc;
          },
          {} as { [key: string]: IClientReq }
        );

        currentClientReqs[aurReqEl.id] = {
          ...currentClientReqs[aurReqEl.id],
          updated_client_reqs: filteredUpdatedReqs
        };

        dispatch(clientReqOp.setClientReqs(currentClientReqs));

        setForceUpdateKey((prev) => prev + 1);
      }

      if (!initialCopiedItems.includes(aurReqEl.id)) {
        // Remove from copied elements state
        const updatedCopiedElements = copiedElements.filter(
          (id) => id !== aurReqEl.id
        );
        dispatch(clientReqOp.setCopiedElements(updatedCopiedElements));
      }
    }
  };

  const extractedClientReqs = () => {
    const reqs = Object.values(clientReqs)
      .map(
        (clientReq: {
          updated_client_reqs: { [key: number]: IClientReq };
          client_reqs: { [key: number]: IClientReq };
        }) => {
          const updatedReqs = Object.values(clientReq.updated_client_reqs).map(
            (req) => ({
              ...req,
              bru_id: String(req.bru_id).startsWith('new-')
                ? undefined
                : req.bru_id
            })
          );
          const newRowReqs = Object.values(clientReq.client_reqs).filter(
            (req) => req.aur_parent_key && req.aur_parent_key.startsWith('new-')
          );
          return [...updatedReqs, ...newRowReqs];
        }
      )
      .flat();

    const filteredReqs = reqs.filter(
      (req) => req && req.value && req.value.trim() !== '' && req.aur_parent_key
    );

    return filteredReqs;
  };

  const getHierarchyData = () => {
    if (mainOrganization) {
      return { main_organization_id: mainOrganization };
    }

    const getFirstValue = (
      val: number[] | number | undefined
    ): number | null => {
      if (Array.isArray(val) && val.length > 0) return val[0];
      if (typeof val === 'number') return val;
      return null;
    };

    const result: Record<string, number> = {};
    const fields: Array<[keyof typeof orgContext, string]> = [
      ['group', 'group_id'],
      ['division', 'division_id'],
      ['region', 'region_id'],
      ['legalEntity', 'legal_entity_id'],
      ['businessUnit', 'business_unit_id']
    ];

    fields.forEach(([key, apiKey]) => {
      const value = getFirstValue(
        orgContext?.[key] as number[] | number | undefined
      );
      if (value) result[apiKey] = value;
    });

    return result;
  };

  // Helper function to find AUR data and add metadata
  const enrichWithAurData = (req: IClientReq) => {
    let aurId: number | undefined;
    let moduleId: number | undefined;
    let category: CategoryList | undefined;

    // Find AUR data
    processAppliedFiltersResult(appliedFiltersResult, (regReq, aurReq) => {
      if (
        req.aur_parent_key === aurReq.aur_parent_key &&
        req.aur_child_key === aurReq.aur_child_key
      ) {
        aurId = aurReq.id;

        if (regReq.module_id) {
          moduleId = regReq.module_id;
        }
        category = changedAurCategories[aurReq.id] || aurReq.category_id;
      }
    });

    const hierarchy = getHierarchyData();

    return {
      ...req,
      ...(aurId && { aur_id: aurId }),
      ...(category !== undefined && { category }),
      ...(moduleId && { module_id: moduleId }),
      ...hierarchy
    };
  };

  const extractedAurCategoryChanges = (): IClientReq[] => {
    return Object.entries(changedAurCategories)
      .map(([aurIdStr, category]) => {
        const aurId = parseInt(aurIdStr);
        let aurReq: IAurReq | undefined;
        let regReq: IRegulatoryReq | undefined;

        processAppliedFiltersResult(appliedFiltersResult, (reg, aur) => {
          if (aur.id === aurId) {
            aurReq = aur;
            regReq = reg;
          }
        });

        if (!aurReq) return null;

        const hierarchy = getHierarchyData();

        // Check if there's an existing client req for this AUR that should be updated
        const clientReqData = clientReqs[aurId];
        let existingClientReq: IClientReq | undefined;

        if (clientReqData) {
          // Look in both client_reqs and updated_client_reqs for existing records
          const allClientReqs = {
            ...clientReqData.client_reqs,
            ...clientReqData.updated_client_reqs
          };

          // Find the first existing record for this AUR
          existingClientReq = Object.values(allClientReqs)[0];
        }

        return {
          aur_parent_key: aurReq.aur_parent_key,
          aur_child_key: aurReq.aur_child_key,
          value: '', // Always empty string for category-only updates
          // Preserve existing bru_id if available, otherwise undefined for new records
          ...(existingClientReq?.bru_id && {
            bru_id: existingClientReq.bru_id
          }),
          ...(existingClientReq?.id &&
            !existingClientReq?.bru_id && { bru_id: existingClientReq.id }),
          aur_id: aurId,
          category,
          module_id: regReq?.module_id || existingClientReq?.module_id,
          ...hierarchy
        } as IClientReq;
      })
      .filter((req): req is IClientReq => req !== null);
  };

  const addAurCategoriesToReqs = (clientReqsParam: IClientReq[]) => {
    return clientReqsParam.map(enrichWithAurData);
  };

  useEffect(() => {
    // Removed filterRegReqs call - now using consolidated processFilteredData in getFilteredDataSource
  }, [tableFilters.applicability, localCheckedAurReqs, appliedFiltersResult]);

  // Use consolidated click outside handlers
  useClickOutside(
    categoryFilterRef,
    showCategoryFilter,
    () => setShowCategoryFilter(false),
    'category-filter-dropdown'
  );
  useClickOutside(
    scopeFilterRef,
    showScopeFilter,
    () => setShowScopeFilter(false),
    'scope-filter-dropdown'
  );
  // Load categories and content types on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getDynamicCategories(lang);
        const sortedCategories = categories.sort(
          (a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
        );
        setCategoryOptions(
          sortedCategories as { value: CategoryList; label: string }[]
        );
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategoryOptions([]);
      }
    };

    const loadContentTypes = async () => {
      try {
        const response = await ApiService.getBruContentTypes();
        const contentTypes = response.data.data || [];
        const options = contentTypes.map(
          (type: { name: string; id: number }) => ({
            label: type.name,
            value: type.id
          })
        );
        const sortedOptions = options.sort(
          (a: { label: string }, b: { label: string }) =>
            a.label.localeCompare(b.label)
        );
        setContentTypeOptions(sortedOptions);
      } catch (error) {
        console.error('Failed to load content types:', error);
        setContentTypeOptions([]);
      }
    };

    loadCategories();
    loadContentTypes();
  }, [lang]);

  useEffect(() => {
    const initialCopiedIds: number[] = [];

    Object.keys(clientReqs).forEach((aurReqIdStr) => {
      const aurReqId = parseInt(aurReqIdStr);
      const clientReq = clientReqs[aurReqId];

      if (clientReq) {
        const hasUpdatedReqs =
          clientReq.updated_client_reqs &&
          Object.keys(clientReq.updated_client_reqs).length > 0;
        const hasExistingReqs =
          clientReq.client_reqs &&
          Object.keys(clientReq.client_reqs).length > 0;

        if (hasUpdatedReqs || hasExistingReqs) {
          initialCopiedIds.push(aurReqId);
        }
      }
    });

    if (initialCopiedIds.length > 0) {
      dispatch(clientReqOp.setCopiedElements(initialCopiedIds));
    }

    if (initialCopiedItems.length === 0) {
      setInitialCopiedItems(initialCopiedIds);
    }
  }, [dispatch, clientReqs]);

  useEffect(() => {
    setCheckedAurReqs();
    setClientReqs();
  }, [appliedFiltersResult]);

  useEffect(() => {
    if (saveClicked) {
      const saveData = extractedClientReqs();
      const categoryOnlyData = extractedAurCategoryChanges();

      const allCopyCheckedItems = checkedElementsToBeCopied.filter(
        (id) => checkedAurReqs.includes(id) && !userDeselectedItems.includes(id)
      );

      const filteredSaveData = saveData.filter((req) => {
        const belongsToCopyCheckedItem = allCopyCheckedItems.some(
          (aurReqId) => {
            let belongsToThisAur = false;
            processAppliedFiltersResult(
              appliedFiltersResult,
              (regReq, aurReq) => {
                if (
                  aurReq.id === aurReqId &&
                  req.aur_parent_key === aurReq.aur_parent_key &&
                  req.aur_child_key === aurReq.aur_child_key
                ) {
                  belongsToThisAur = true;
                }
              }
            );
            return belongsToThisAur;
          }
        );
        return !belongsToCopyCheckedItem;
      });

      const combinedData = [...filteredSaveData];
      categoryOnlyData.forEach((categoryReq) => {
        if (
          !filteredSaveData.some(
            (req) =>
              req.aur_parent_key === categoryReq.aur_parent_key &&
              req.aur_child_key === categoryReq.aur_child_key
          )
        ) {
          combinedData.push(categoryReq);
        }
      });

      const currentlyCheckedItems = checkedElementsToBeCopied.filter(
        (id) => !userDeselectedItems.includes(id)
      );

      const potentialNewItems = currentlyCheckedItems.filter((id) => {
        return !initialCopiedItems.includes(id);
      });

      const newlySelectedItems = potentialNewItems.filter((id) =>
        checkedAurReqs.includes(id)
      );

      const alreadyCopiedItems = currentlyCheckedItems.filter((id) => {
        return initialCopiedItems.includes(id) && checkedAurReqs.includes(id);
      });

      // Create copies for ALL items that are checked for copying
      const allCopyItemCopies: IClientReq[] = [];

      allCopyCheckedItems.forEach((aurReqId) => {
        processAppliedFiltersResult(appliedFiltersResult, (regReq, aurReq) => {
          if (aurReq.id === aurReqId) {
            const newAurCopy: IClientReq = {
              aur_parent_key: aurReq.aur_parent_key,
              aur_child_key: aurReq.aur_child_key,
              ...{ value: aurReq.value, bru_id: undefined, id: undefined }
            };
            allCopyItemCopies.push(newAurCopy);
          }
        });
      });

      const finalSaveData = addAurCategoriesToReqs([
        ...combinedData,
        ...allCopyItemCopies
      ]);

      const applicabilities = Object.values(changedApplicability);
      const categories = Object.values(changedCategories);

      const contentTypeData = Object.entries(selectedContentType)
        .filter(
          ([aurReqId, contentTypeId]) => changedContentType[parseInt(aurReqId)]
        )
        .map(([aurReqId, contentTypeId]) => {
          const hierarchy = getHierarchyData();
          return {
            bru_id: parseInt(aurReqId),
            bru_content_type_id: contentTypeId,
            bru_definition:
              contentTypeDefinition[parseInt(aurReqId)]?.[contentTypeId] || '',
            table_id: generateUniqueId(),
            ...hierarchy
          };
        });

      const hasCopiedItems =
        checkedElementsToBeCopied.length > 0 &&
        checkedElementsToBeCopied.some((id) => isAurReqCopied(id));

      if (
        finalSaveData.length > 0 ||
        applicabilities.length > 0 ||
        categories.length > 0 ||
        Object.keys(changedAurCategories).length > 0 ||
        contentTypeData.length > 0 ||
        hasCopiedItems
      ) {
        const savePromises = [];

        if (finalSaveData.length > 0) {
          // Separate updates from new records with stronger validation
          const updates = finalSaveData.filter(isValidBruId);
          const newRecords = finalSaveData.filter(
            (item) => !isValidBruId(item)
          );

          // Process updates and new records separately
          const processedUpdates =
            updates.length > 0
              ? applyFiltersToData(updates, filterParams, {
                  generateBruId: false
                })
              : [];
          const processedNewRecords =
            newRecords.length > 0
              ? applyFiltersToData(newRecords, filterParams, {
                  generateBruId: true
                })
              : [];

          const allProcessedData = [
            ...processedUpdates,
            ...processedNewRecords
          ];

          if (allProcessedData.length > 0) {
            savePromises.push(
              dispatch(clientReqOp.saveClientReqs(allProcessedData, lang, true))
            );
          }
        }

        if (applicabilities.length > 0) {
          savePromises.push(
            dispatch(clientReqOp.updateApplicability(applicabilities, lang))
          );
        }

        if (categories.length > 0) {
          savePromises.push(
            dispatch(clientReqOp.updateCategory(categories, lang))
          );
        }

        if (contentTypeData.length > 0) {
          const hierarchyData = getHierarchyData();

          if (Object.keys(hierarchyData).length === 0) {
            notification.error({
              message: 'Error',
              description:
                'Please select either a hierarchical level or main organization before saving',
              placement: 'topRight',
              duration: 3
            });
            throw new Error('No hierarchy or main organization selected');
          }

          const contentTypePromises = contentTypeData.map(async (data) => {
            const payloadWithHierarchy = {
              ...data,
              ...hierarchyData
            };
            const response = await ApiService.saveBruRiskControl(
              payloadWithHierarchy
            );
            return response;
          });
          savePromises.push(...contentTypePromises);
        }

        Promise.all(savePromises)
          .then((results) => {
            setChangedApplicability({});
            setChangedCategory({});
            setChangedClientCategory({});
            setChangedAurCategory({});
            setChangedContentType({});
            dispatch(clientReqOp.setCheckedElementsToBeCopied([]));
            const clearedClientReqs = Object.entries(clientReqs).reduce(
              (acc, [key, value]) => {
                acc[key] = {
                  ...value,
                  updated_client_reqs: {}
                };
                return acc;
              },
              {} as typeof clientReqs
            );
            dispatch(clientReqOp.setClientReqs(clearedClientReqs));

            dispatch(complianceEstOp.setDrawTable(true));

            if (
              categories.length > 0 ||
              Object.keys(changedAurCategories).length > 0 ||
              contentTypeData.length > 0 ||
              finalSaveData.length > 0
            ) {
              const backendMessage = (results as any[])
                .map((r: any) => r?.data?.message || r?.payload?.data?.message)
                .find(Boolean);
              notification.success({
                message: 'Success',
                description:
                  backendMessage || 'Business rule updated successfully',
                placement: 'topRight',
                duration: 3
              });
            }

            setTimeout(async () => {
              const baseParams = Helpers.toApiFilters(orgContext);
              const moduleNames = Array.from(
                new Set(
                  appliedFiltersResult
                    ?.map((item: IRegulatoryReq) => item.module_name)
                    .filter(Boolean) || []
                )
              );

              const refreshFilterParams = {
                ...baseParams,
                ...(moduleNames.length > 0 && { module: moduleNames })
              };

              const data = await complianceEstOp.applyFiltersApi(
                lang,
                refreshFilterParams
              );
              if (data && Object.keys(data).length > 0) {
                dispatch(clientReqOp.setAppliedFilersResult(data));
              }
            }, 500);

            dispatch(complianceEstOp.setSaveDisabled(true));
          })
          .catch((error) => {
            notification.error({
              message: 'Error',
              description: 'Failed to save business rule',
              placement: 'topRight',
              duration: 3
            });
          });
      }

      dispatch(complianceEstOp.setSaveClicked(false));
    }
  }, [
    saveClicked,
    selectedContentType,
    changedApplicability,
    changedCategories,
    changedClientCategories,
    changedAurCategories,
    changedContentType,
    contentTypeDefinition,
    clientReqs,
    lang,
    checkedElementsToBeCopied
  ]);

  // Keep merged/copied lists synced with checkedAurReqs
  useEffect(() => {
    const updatedMergedElements = checkedElementsToBeMerged.filter(
      (el: number) => checkedAurReqs.includes(el)
    );

    dispatch(clientReqOp.setCheckedElementsToBeMerged(updatedMergedElements));
  }, [checkedAurReqs, dispatch]);

  useEffect(() => {
    const newClnReqs = Object.values(clientReqs).map(
      (clnReqs) => clnReqs.updated_client_reqs
    );

    const hasCopiedItems = checkedElementsToBeCopied.length > 0;

    const isEmpty =
      newClnReqs.every((clnReq) => Helpers.isObjEmpty(clnReq)) &&
      Helpers.isObjEmpty(changedApplicability) &&
      Helpers.isObjEmpty(changedCategories) &&
      Helpers.isObjEmpty(changedClientCategories) &&
      Helpers.isObjEmpty(changedAurCategories) &&
      Helpers.isObjEmpty(changedContentType) &&
      !hasCopiedItems;

    if (isEmpty) {
      dispatch(complianceEstOp.setSaveDisabled(true));
    } else {
      dispatch(complianceEstOp.setSaveDisabled(false));
    }
  }, [
    clientReqs,
    changedApplicability,
    changedCategories,
    changedClientCategories,
    changedAurCategories,
    changedContentType,
    checkedElementsToBeCopied,
    dispatch,
    lang
  ]);
  const isReadyToApplyFilters =
    !!lang &&
    Array.isArray(orgContext?.industryType) &&
    orgContext.industryType.length > 0;

  const columns = [
    {
      title: () => (
        <Styled.ColumnHeaderContainerModule>
          <Styled.ModuleHeading>{i18n.t('MODULE')}</Styled.ModuleHeading>
        </Styled.ColumnHeaderContainerModule>
      ),
      dataIndex: 'value',
      key: 'value',
      width: 40,
      fixed: 'left',
      render: (value: string, record: IRegulatoryReq) => {
        return (
          <Styled.Container>
            <Styled.ContentRow>
              {record?.module_name && (
                <Popover
                  content={
                    <span>
                      <b>Module:</b> {record.module_name}
                    </span>
                  }
                >
                  <Styled.ModuleBadge scheme={scheme} token={token}>
                    {record.module_name}
                  </Styled.ModuleBadge>
                </Popover>
              )}
            </Styled.ContentRow>
          </Styled.Container>
        );
      }
    },
    {
      title: () => (
        <Styled.ColumnHeaderContainer>
          <Styled.RegulatoryReqHeading>
            {i18n.t('REGULATORY_REQUIREMENTS')}
          </Styled.RegulatoryReqHeading>
          <Styled.SearchContainer>
            <Styled.SearchIcon
              scheme={scheme}
              onClick={() => setShowRegReqSearch(!showRegReqSearch)}
            />
            {showRegReqSearch && (
              <Styled.SearchInput
                placeholder='Search'
                value={regReqSearchText}
                onChange={(e) => setRegReqSearchText(e.target.value)}
                autoFocus
              />
            )}
          </Styled.SearchContainer>
        </Styled.ColumnHeaderContainer>
      ),
      dataIndex: 'value',
      key: 'value',
      width: showRegReqSearch ? 600 : 450,
      render: (value: string, record: IRegulatoryReq) => {
        return (
          <Styled.Container>
            <Styled.ContentRow>
              <Styled.ParsedTextContainer
                level={1}
                width={showRegReqSearch ? 600 : 500}
                scheme={scheme}
                token={token}
              >
                {renderFormattedText(value, scheme, token)}
              </Styled.ParsedTextContainer>
            </Styled.ContentRow>
          </Styled.Container>
        );
      }
    },
    {
      title: () => (
        <Styled.TableHeaderGrid
          isExpanded={showClientSearch}
          isAurExpanded={showAurSearch}
          isRegReqExpanded={showRegReqSearch}
          dragOffset={Math.max(...Object.values(aurDragOffset), 0)}
          token={token}
          isScrolled={scrollLeft > 0}
          theme={scheme as unknown as ThemeList}
        >
          <Styled.HeaderGridColumn scheme={scheme}>
            <Styled.ScopeHeaderContainer
              ref={scopeFilterRef}
              scheme={scheme}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowScopeFilter(!showScopeFilter);
              }}
            >
              <span>{i18n.t('APPLICABILITY')}</span>
              <Styled.ScopeFilterIcon scheme={scheme} />
              {renderScopeDropdown()}
            </Styled.ScopeHeaderContainer>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <Styled.HeaderWithSearch>
              <Styled.InterpretationBaselineHeading>
                {i18n.t('AUR_INTERPRETED_REQUIREMENTS')}
              </Styled.InterpretationBaselineHeading>
              <Styled.SearchContainer isAurSearch={true}>
                <Styled.SearchIcon
                  scheme={scheme}
                  isAur={true}
                  onClick={() => {
                    setShowAurSearch(!showAurSearch);
                  }}
                />
                {showAurSearch && (
                  <Styled.SearchInput
                    placeholder='Search'
                    value={aurSearchText}
                    onChange={(e) => setAurSearchText(e.target.value)}
                    autoFocus
                  />
                )}
              </Styled.SearchContainer>
            </Styled.HeaderWithSearch>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <Styled.CategoryHeaderContainer
              ref={categoryFilterRef}
              scheme={scheme}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowCategoryFilter(!showCategoryFilter);
              }}
            >
              <span>{i18n.t('CATEGORY')}</span>
              <Styled.CategoryFilterIcon scheme={scheme} />
              {renderCategoryDropdown()}
            </Styled.CategoryHeaderContainer>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <span>{i18n.t('MERGE')}</span>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <span>{i18n.t('COPY')}</span>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <Styled.TargetArtefactHeading>
              {i18n.t('TARGET_ARTEFACT_DEFINITION')}
            </Styled.TargetArtefactHeading>
          </Styled.HeaderGridColumn>
          <Styled.HeaderGridColumn scheme={scheme}>
            <Styled.HeaderWithSearch scheme={scheme}>
              <span>{i18n.t('CLIENT_INTERPRETATION_BASELINE')}</span>
              <Styled.SearchContainer>
                <Styled.ClientSearchIcon
                  scheme={scheme}
                  onClick={() => setShowClientSearch(!showClientSearch)}
                />
                {showClientSearch && (
                  <Styled.ClientSearchInput
                    placeholder='Search'
                    value={clientSearchText}
                    onChange={(e) => setClientSearchText(e.target.value)}
                    autoFocus
                  />
                )}
              </Styled.SearchContainer>
            </Styled.HeaderWithSearch>
          </Styled.HeaderGridColumn>
        </Styled.TableHeaderGrid>
      ),
      dataIndex: 'AUR_REQ',
      key: 'clnReqs',
      width: showClientSearch ? 700 : 500,
      filterIcon: () => null,
      filtered:
        tableFilters.applicability?.[0] !== undefined &&
        tableFilters.applicability[0] !== 'all',
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters
      }: FilterDropdownProps) => (
        <Styled.ApplicabilityFilterDropdown>
          <Styled.FilterTitle>{i18n.t('SCOPE')}</Styled.FilterTitle>
          <Styled.FilterButtonContainer>
            <Styled.FilterButton
              size='small'
              type='primary'
              onClick={() => {
                handleSelectAll();
                confirm();
              }}
            >
              Select All
            </Styled.FilterButton>
            <Styled.FilterButton
              size='small'
              onClick={() => {
                handleDeselectAll();
                confirm();
              }}
            >
              Deselect All
            </Styled.FilterButton>
          </Styled.FilterButtonContainer>
          <FilterDropdown
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            options={applicabilityOptions}
            defaultValue='all'
            filterName='applicability'
          />
        </Styled.ApplicabilityFilterDropdown>
      ),
      render: (aurReq: IAurReq, record: IRegulatoryReq, index: number) => {
        const aurReqs = normalizeAurReqs(aurReq);

        if (aurReqs.length === 0) {
          const emptyAurReq = {
            id: 0,
            value: '',
            level: 1,
            aur_parent_key: `empty-${record.id}`,
            aur_child_key: `empty-${record.id}`,
            is_app: YesNo.NO,
            CLN_REQ: {}
          };
          aurReqs.push(emptyAurReq);
        }

        const currentFilteredDataSource = getFilteredDataSource();
        const isLastRecord = index === currentFilteredDataSource.length - 1;

        return (
          <>
            {aurReqs.map((firstAurReq: IAurReq, aurReqIndex: number) => {
              const isLastAurInRecord =
                aurReqIndex === aurReqs.length - 1 && aurReqs.length > 1;

              // Create a unique key that combines record ID, AUR ID, and index
              const uniqueKey = `${record.id}-${firstAurReq.id}-${aurReqIndex}`;

              return (
                <Styled.AurInterpretationWrapper
                  key={uniqueKey}
                  scheme={scheme}
                  isLastAurInRecord={isLastAurInRecord && isLastRecord}
                >
                  <Styled.TableRowGrid
                    isExpanded={showClientSearch}
                    isAurExpanded={showAurSearch}
                    isRegReqExpanded={showRegReqSearch}
                    scheme={scheme}
                    dragOffset={aurDragOffset[firstAurReq.id] || 0}
                  >
                    <Styled.GridColumn>
                      <Styled.ScopeCheckbox
                        checked={localCheckedAurReqs.includes(firstAurReq.id)}
                        onChange={(e: CheckboxChangeEvent) => {
                          handleCheckboxChange(
                            firstAurReq.id,
                            e.target.checked
                          );
                        }}
                        disabled={firstAurReq.id === 0}
                      />
                    </Styled.GridColumn>
                    <Styled.GridColumn>
                      <Styled.AurTextArea
                        defaultValue={firstAurReq.value}
                        rows={3}
                        placeholder='AUR Requirement'
                        readOnly={true}
                        disabled={true}
                        scheme={scheme}
                        token={token}
                        onMouseDown={(e) => {
                          const startX = e.clientX;

                          const handleMouseMove = (moveEvent: MouseEvent) => {
                            const deltaX = moveEvent.clientX - startX;
                            handleAurDrag(firstAurReq.id, deltaX);
                          };

                          const handleMouseUp = () => {
                            document.removeEventListener(
                              'mousemove',
                              handleMouseMove
                            );
                            document.removeEventListener(
                              'mouseup',
                              handleMouseUp
                            );
                          };

                          document.addEventListener(
                            'mousemove',
                            handleMouseMove
                          );
                          document.addEventListener('mouseup', handleMouseUp);
                        }}
                      />
                    </Styled.GridColumn>
                    <Styled.GridColumn>
                      <Styled.CategoryContainer>
                        <CustomSelect
                          options={categoryOptions}
                          value={
                            changedAurCategories[firstAurReq.id] ||
                            firstAurReq.category_id
                          }
                          onChange={(value: CategoryList) => {
                            handleAurCategoryChange(firstAurReq.id, value);
                          }}
                          placeholder='Select category'
                          allowClear
                          disabled={firstAurReq.id === 0}
                        />
                      </Styled.CategoryContainer>
                    </Styled.GridColumn>
                    <Styled.GridColumn>
                      <CustomCheckbox
                        checked={checkedElementsToBeMerged.includes(
                          firstAurReq.id
                        )}
                        disabled={
                          firstAurReq.id === 0 ||
                          isAurReqUnchecked(firstAurReq) ||
                          checkedElementsToBeCopied.includes(firstAurReq.id) ||
                          mergedElements.includes(firstAurReq.id) ||
                          copiedElements.includes(firstAurReq.id)
                        }
                        onChange={() => handleMergeChange(firstAurReq)}
                      />
                    </Styled.GridColumn>
                    <Styled.GridColumn>
                      {(() => {
                        const isInCheckedToBeCopied =
                          checkedElementsToBeCopied.includes(firstAurReq.id);

                        const isDisabled =
                          firstAurReq.id === 0 ||
                          isAurReqUnchecked(firstAurReq) ||
                          checkedElementsToBeMerged.includes(firstAurReq.id) ||
                          mergedElements.includes(firstAurReq.id);

                        return (
                          <CustomCheckbox
                            checked={isInCheckedToBeCopied}
                            disabled={isDisabled}
                            onChange={(e: CheckboxChangeEvent) => {
                              handleCopyCheck(e, firstAurReq);
                            }}
                          />
                        );
                      })()}
                    </Styled.GridColumn>
                    <Styled.GridColumn>
                      <Styled.TargetArtefactContainer>
                        <Styled.ContentTypeSelect>
                          <CustomSelect
                            value={selectedContentType[firstAurReq.id]}
                            options={contentTypeOptions}
                            placeholder='Select type'
                            allowClear
                            onChange={(value: number) => {
                              setSelectedContentType((prev) => {
                                const next = { ...prev };
                                if (value == null) delete next[firstAurReq.id];
                                else next[firstAurReq.id] = value;
                                return next;
                              });
                              setChangedContentType((prev) => ({
                                ...prev,
                                [firstAurReq.id]: true
                              }));
                            }}
                            disabled={firstAurReq.id === 0}
                          />
                        </Styled.ContentTypeSelect>
                        {selectedContentType[firstAurReq.id] && (
                          <Styled.DefinitionInput
                            value={
                              contentTypeDefinition[firstAurReq.id]?.[
                                selectedContentType[firstAurReq.id]
                              ] || ''
                            }
                            placeholder='Enter definition'
                            onChange={(e) => {
                              const contentTypeId =
                                selectedContentType[firstAurReq.id];

                              setContentTypeDefinition((prev) => ({
                                ...prev,
                                [firstAurReq.id]: {
                                  ...(prev[firstAurReq.id] || {}),
                                  [contentTypeId]: e.target.value
                                }
                              }));

                              setChangedContentType((prev) => ({
                                ...prev,
                                [firstAurReq.id]: true
                              }));
                            }}
                            disabled={firstAurReq.id === 0}
                          />
                        )}
                        <Popover content='Segment No.' trigger='hover'>
                          <div>
                            <Styled.ClientReqTextArea
                              value={
                                firstAurReq.id ? firstAurReq.id.toString() : ''
                              }
                              rows={1}
                              className='bru-field'
                              placeholder='BRU ID'
                              readOnly={true}
                              disabled={true}
                              scheme={scheme}
                            />
                          </div>
                        </Popover>
                      </Styled.TargetArtefactContainer>
                    </Styled.GridColumn>
                    <Styled.ClientReqColumn
                      token={token}
                      theme={scheme as unknown as ThemeList}
                    >
                      <TextAreaBox
                        key={`${firstAurReq.id}-${forceUpdateKey}`}
                        type='cln_req'
                        aurReq={firstAurReq}
                        aurReqId={firstAurReq.id}
                        plusDisabled={
                          firstAurReq.id === 0 || isAurReqUnchecked(firstAurReq)
                        }
                        onDelete={handleBruDelete}
                      />
                    </Styled.ClientReqColumn>
                  </Styled.TableRowGrid>
                </Styled.AurInterpretationWrapper>
              );
            })}
          </>
        );
      }
    }
  ];

  const filteredDataSource = getFilteredDataSource();
  const safeDataSource = isReadyToApplyFilters ? filteredDataSource : [];

  // Check if any dropdowns are currently visible
  const hasVisibleDropdowns = showCategoryFilter || showScopeFilter;
  useEffect(() => {
    const tableWrapper = tableContainerRef.current;

    console.log('Table wrapper:', tableWrapper);

    const scrollContainer = tableWrapper?.querySelector(
      '.ant-table-body'
    ) as HTMLElement | null;

    const topScrollbar = topScrollbarRef.current;
    const topScrollbarContent = topScrollbarContentRef.current;

    console.log('Scroll container:', scrollContainer);

    if (!scrollContainer || !topScrollbar || !topScrollbarContent) {
      console.warn('Scrollbar initialization stopped: missing element');
      return;
    }

    const updateScrollbarWidth = () => {
      const table = scrollContainer.querySelector('table');

      const contentWidth = Math.max(
        scrollContainer.scrollWidth,
        table?.scrollWidth || 0
      );

      topScrollbarContent.style.width = `${contentWidth}px`;
      topScrollbar.scrollLeft = scrollContainer.scrollLeft;
    };

    const handleTopScroll = () => {
      scrollContainer.scrollLeft = topScrollbar.scrollLeft;
    };

    const handleTableScroll = () => {
      topScrollbar.scrollLeft = scrollContainer.scrollLeft;
      setScrollLeft(scrollContainer.scrollLeft);
    };

    updateScrollbarWidth();

    topScrollbar.addEventListener('scroll', handleTopScroll);
    scrollContainer.addEventListener('scroll', handleTableScroll);

    const resizeObserver = new ResizeObserver(updateScrollbarWidth);
    resizeObserver.observe(scrollContainer);

    const table = scrollContainer.querySelector('table');
    if (table) resizeObserver.observe(table);

    window.addEventListener('resize', updateScrollbarWidth);

    return () => {
      topScrollbar.removeEventListener('scroll', handleTopScroll);
      scrollContainer.removeEventListener('scroll', handleTableScroll);
      window.removeEventListener('resize', updateScrollbarWidth);
      resizeObserver.disconnect();
    };
  }, [drawTable, loading, filteredDataSource.length, isReadyToApplyFilters]);

  return (
    <>
      <Styled.ModuleColumnGlobalStyle />
      <ComplianceEstLayout>
        <Styled.ContentWrapper
          token={token}
          className='nested-table cln-req-wrapper'
        >
          <Styled.ContentWrapper
            token={token}
            className='nested-table cln-req-wrapper'
          >
            <div ref={tableContainerRef}>
              <Styled.TopTableScrollbar ref={topScrollbarRef}>
                <Styled.TopTableScrollbarContent ref={topScrollbarContentRef} />
              </Styled.TopTableScrollbar>

              <Styled.StyledCustomTable
                drawTable={drawTable}
                itemsCount={filteredDataSource.length}
                columns={columns}
                dataSource={safeDataSource}
                loading={loading}
                rowKey={(record: IRegulatoryReq) => record.id}
                scheme={scheme}
                hasVisibleDropdowns={hasVisibleDropdowns}
                scroll={{
                  x: 'max-content'
                }}
              />
            </div>
          </Styled.ContentWrapper>
        </Styled.ContentWrapper>
      </ComplianceEstLayout>
    </>
  );
};

export default ClientRequirements;
